import { cva } from 'class-variance-authority';

/**
 * Alert class recipes.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 639:77378), re-read after
 * the September 2026 rework. The set is now FIVE cells on one `Type` axis — the Mode axis is gone,
 * because every colour in it moved onto the `🌙 Modes` semantic layer and therefore flips with the
 * theme rather than with a variant. `Title` and `isDismissible` are boolean properties that toggle a
 * layer's visibility, not variants; `↪ Title` and `↪ Description` are text properties.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-alert-*` slots, which the `.ds-alert-<type>` classes in
 * `packages/design-system/src/styles/design-system.css` set. Those classes now have no dark counterpart at all —
 * see the note there.
 *
 * The geometry is uniform. There is no Size axis, and nothing below changes with the severity:
 *
 *   panel     1px border, `radius/md` (16px), `x4` inset, `x3` column gap
 *   icon      `x5` (20px) glyph, top-aligned
 *   content   `x1` row gap, `x5` right inset
 *   title     16/26 Medium   (Figma `Heading/xs` -> `text-heading-xs`)
 *   body      14/20 Regular  (Figma `Body/md`   -> `text-body-md`)
 *   dismiss   `x6` (24px) button carrying an `x4` (16px) glyph, pinned `x3,5` (14px) from the top
 *             and right and OUT of the flow — see `alertDismissRecipe`
 *
 * Every one of those is a NAMED spacing step rather than an arbitrary pixel value, because the
 * reworked Figma binds each of them to the `📏 Spacing` collection (`x1`, `x3`, `x3,5`, `x4`, `x5`,
 * `x6`) and `--spacing: 4px` makes `gap-3` and `x3` the same number by construction. The radius is
 * `rounded-md`, which reads `--radius-md` — the one Tailwind namespace this layer claims. The
 * 2026-09-24 rework moved it from `radius/sm` (6px) to `radius/md` (16px).
 *
 * Three things the previous port did that the reworked set does NOT draw, all reverted here:
 *
 *   - a 1.5px border. The set draws 1px.
 *   - a dismiss control in the flex flow, which made the panel 4px taller whenever a title-less
 *     alert was dismissible and pushed the text column 32px clear of the ×. The set positions it
 *     absolutely.
 *   - an icon and a dismiss control centred on the first text line. The set top-aligns both, so
 *     `--ds-alert-line` and the `hasTitle` variant are gone.
 *   - a glyph coloured separately from the text. The glyph inherits `currentColor`, so it is always
 *     the text's colour, and `--ds-alert-icon` is gone. Since the 2026-09-24 rework that colour is
 *     the severity's own foreground role (Neutral stays `foreground/primary`) — see
 *     `.ds-alert-<type>` in `design-system.css`. Severity is still carried three ways — the panel's
 *     colour, the glyph's SHAPE, and the politeness of the announcement — so none of it rests on
 *     hue alone.
 *
 * The set draws the panel 624px wide, which is the FRAME rather than the component: the page's own
 * examples put the same alert at 720px and at 318px. So the root is `w-full` and the width belongs
 * to the caller, as it does for the text input.
 *
 * ── NO MOTION, deliberately, and it is the only component in this layer with none ─────────────
 *
 * The dismiss control is an `IconButton`, so it answers a press exactly as every other button does
 * and this file needs to say nothing about it. What the ALERT does not have is an entrance, and the
 * reason is a lifecycle one rather than a taste one.
 *
 * An entrance must fire on absent->present ONLY. A recipe cannot tell a first appearance from a
 * route restoration remounting the same alert, so an entrance written here would re-slide on every
 * mount: back-navigation, a parent re-key, a Suspense boundary resolving. The four popups in this
 * layer can animate because Base UI gives them `data-starting-style` and `data-ending-style` — a
 * real open/close lifecycle with a controlled `open` prop behind it. An alert has neither: the
 * caller mounts and unmounts it, so there is no moment this file could key off.
 *
 * Doing it properly needs a presence API on the component — "has this been seen", and a decision
 * about where that persists — plus a caller willing to defer its own unmount so an exit has time to
 * play. That is a change of its own, not a line in this file.
 *
 * Stated rather than left blank on purpose, the same way the missing Figma loading cell is stated in
 * `Button.recipe.ts`: an absence with a reason is documentation, an unexplained one reads as an
 * oversight and gets "fixed".
 */

/**
 * The panel.
 *
 * `items-start` is both what the set draws and what stays right once the description wraps: the icon
 * stays beside the FIRST line rather than drifting to the centre of a three-line block.
 *
 * `relative` is the containing block for the dismiss control, which the set takes out of the flow
 * entirely. See `alertDismissRecipe`.
 *
 * `severity` selects a slot class rather than carrying colour itself, so this file has no hex values
 * and no theme branches at all. It has NO default: which severity is the design's default is an open
 * question on HNS-339 (the Figma set says Neutral; the shipped MUI wrapper defaults to a white
 * `info`), and defaulting either way here would answer it by accident. A call site must say what
 * kind of message it is showing.
 */
export const alertRecipe = cva(
  [
    'ds-alert',
    'relative flex w-full items-start gap-3',
    'rounded-md border-solid border-[length:1px] p-4',
    'font-sans',
    'bg-[var(--ds-alert-bg)] border-[var(--ds-alert-border)] text-[var(--ds-alert-fg)]',
  ],
  {
    variants: {
      severity: {
        neutral: 'ds-alert-neutral',
        info: 'ds-alert-info',
        success: 'ds-alert-success',
        warning: 'ds-alert-warning',
        error: 'ds-alert-error',
      },
    },
  }
);

/**
 * The leading severity glyph.
 *
 * A plain 20px square, top-aligned with the panel's own inset — which is what the set draws, and
 * puts the glyph's centre on the title's cap height rather than on its line box.
 *
 * No colour of its own. The set draws each glyph in the same role as its text, so the icon inherits
 * `--ds-alert-fg` through `currentColor` and there is nothing left to set. The committed 20px
 * exports carry `currentColor` throughout, which is what makes that work.
 *
 * `[&_svg]:size-5` rather than leaving the svg to its own attributes: the exports are 20px already,
 * but stating it here is what keeps the box and the glyph from being able to disagree.
 *
 * `text-[length:20px]` is for a MUI-backed glyph passed in as a toast's `icon` (the raised hand).
 * `SvgIcon` draws at `1em` in UNLAYERED Emotion, which beats the layered `size-5`, so the slot's
 * font-size is what sizes it. Same split as `tooltipIconRecipe`.
 */
export const alertIconRecipe = cva([
  'flex size-5 shrink-0 items-center justify-center text-[length:20px]',
  '[&_svg]:size-5',
]);

/**
 * The text column.
 *
 * `min-w-0` and `flex-1` together are what let a long unbroken string (a URL, an event name) shrink
 * this column instead of pushing the dismiss control off the panel — a flex item's default
 * `min-width: auto` would otherwise refuse to go below its content's intrinsic width.
 *
 * `pr-5` is the set's own 20px right inset on this container (node 639:77164), and it is NOT
 * conditional on the dismiss control: the padding lives on the container in Figma, so it stays when
 * `isDismissible` hides the button. Measured in the file, the column is 656px wide inside a 720px
 * panel in EVERY combination of the two booleans — which is how you can tell the × is out of the
 * flow. With the panel's own `p-4`, the last word therefore sits 36px from the panel's right edge,
 * and the × starts 38px from it.
 *
 * `word-break: break-word` is the design's own setting on the same container, and it is the half of
 * the pair that handles a single word longer than the column.
 */
export const alertContentRecipe = cva(['flex min-w-0 flex-1 flex-col gap-1 pr-5', '[word-break:break-word]']);

/** Figma `Heading/xs`. A step UP from the description, which is `Body/md`. */
export const alertTitleRecipe = cva(['m-0 text-heading-xs']);

/** Figma `Body/md`. Colour is inherited from the panel, so this sets none. */
export const alertDescriptionRecipe = cva(['m-0 text-body-md']);

/**
 * The actions row. Not drawn by the set — see `actions` in `types.ts`.
 *
 * `mt-2` on top of the column's `gap-1` puts 12px between the message and the controls, which is
 * the `x3` step the panel already uses for its own column gap. `flex-wrap` lets a second control
 * drop under the first on a narrow panel rather than squeezing either label.
 */
export const alertActionsRecipe = cva(['mt-2 flex flex-wrap items-center gap-2']);

/**
 * The dismiss control.
 *
 * Only the two things the shared button cannot know, applied on top of `IconButton` at `size="2xs"`
 * with `muted`/`default` — so the alert does not hand-roll a button: it inherits the focus ring, the
 * hover and pressed backgrounds, the disabled treatment and the 24px box from the same recipe every
 * other DS button uses.
 *
 * `absolute top-3.5 right-3.5` is the set's own `layoutPositioning: 'ABSOLUTE'` with constraints
 * `{horizontal: 'MAX', vertical: 'MIN'}` at x,y = 14px from the top-right — NOT the 16px the panel's
 * padding would suggest, and not a flex item. Two consequences that only show up in a real layout,
 * both of which the previous in-flow port got wrong:
 *
 *   - the panel's height ignores the button. A dismissible alert with no title is 52px in Figma, the
 *     height of its one line of text; in flow the 24px button made it 56px.
 *   - the text column is the same width whether or not the alert is dismissible, so turning
 *     dismissal on never reflows the message.
 *
 * It overlaps the container's right padding by 2px, which is what the set draws and is invisible in
 * practice — a line of text would have to fill its column to the last pixel to reach it.
 *
 * `--ds-btn-icon-size` is overridden to 16px because that is what Figma's `Size=ExtraSmall` cell
 * draws (24px box, `x1` padding, `x4` glyph) and what the reworked alert therefore draws. The shared
 * `2xs` cell still says 20px, and correcting it THERE would also move every legacy MUI `small` icon
 * button, which `Buttons/IconButton/toDsIconProps` maps onto the same cell — a wider change than an
 * alert port should make. Recorded as a Button finding instead. `cn()` puts this after the recipe's
 * own declaration, and `tailwind-merge` treats two arbitrary properties with the same name as a
 * conflict, so the 16px wins.
 *
 * `muted`/`default` is the only borderless cell the design draws. What it adds is a hover and
 * pressed background, and those are translucent overlays (`rgb(0 0 0 / 0.08)` light, `rgb(255 255
 * 255 / 0.12)` dark) rather than opaque neutrals — so on the four coloured panels they read as a
 * darker or lighter shade of the panel's own hue rather than as a grey patch on top of it. The
 * design specifies no hover for this control at all; borrowing the button's is what lets the alert
 * have one without inventing five colours.
 *
 * The three `--ds-btn-fg*` overrides make the × the alert's text colour — `foreground/destructive`
 * on an error, `foreground/primary` on Neutral — instead of the button's own neutral. Requested by
 * design on 2026-09-24 for the four coloured severities; the Figma set still draws the × in
 * `foreground/primary` and needs a per-variant override on the nested glyph to match. It is done
 * here rather than as a new Button variant because the colour belongs to the alert, not the button:
 * every other `muted`/`default` button keeps its neutral. Hover and pressed hold the same colour,
 * because the overlay already carries the state, as it does for the neutral label.
 */
export const alertDismissRecipe = cva([
  'absolute top-3.5 right-3.5',
  '[--ds-btn-icon-size:16px]',
  '[--ds-btn-fg:var(--ds-alert-fg)] [--ds-btn-fg-hover:var(--ds-alert-fg)] [--ds-btn-fg-pressed:var(--ds-alert-fg)]',
]);
